import { Ionicons } from '@expo/vector-icons';
import { FlashList } from "@shopify/flash-list";
import React from "react";
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import tw from "twrnc";

import PlayerAvatar from "../../../components/PlayerAvatar";
import { t } from "../../../i18n/translations";
import { iqesColors } from "../../../shared/ui/iqesDesignSystem";
import Tooltip from "../../../shared/ui/Tooltip";
import type { Player } from "../../../types/multiplayer";
import { useLanguageStore } from "../../quiz-core/languageStore";
import { uiText } from '../../../shared/utils/uiText';

interface PlayersListProps {
  /** Accessibility props forwarded to the underlying FlatList */
  accessibilityLabel?: string;
  accessibilityRole?: string;
  accessible?: boolean;
  players: Player[];
  hostId: string | null;
  currentUserId: string | null | undefined;
  isCurrentPlayerReady: boolean;
  onToggleReady: () => Promise<void>;
  loadingStart: boolean;
}

// Helper function to get the best available player display name
const getPlayerDisplayName = (player: Player): string => {
  // Priority order: name -> nickname -> user_id fallback -> default
  if (player.name && player.name.trim() && player.name !== 'Player') {
    return player.name;
  }
  if (player.nickname && player.nickname.trim() && player.nickname !== 'Player') {
    return player.nickname;
  }
  if (player.user_id) {
    return `User ${player.user_id.slice(-4)}`;
  }
  return 'Anonymous Player';
};

const PlayersList: React.FC<PlayersListProps> = ({
  players,
  hostId,
  currentUserId,
  isCurrentPlayerReady,
  onToggleReady,
  loadingStart,
}) => {
  const { language } = useLanguageStore();
  // Tooltip state for badges
  const [tooltipHostIndex, setTooltipHostIndex] = React.useState<number>(-1);
  const [tooltipYouIndex, setTooltipYouIndex] = React.useState<number>(-1);
  const [tooltipReadyIndex, setTooltipReadyIndex] = React.useState<number>(-1);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleToggleReady = React.useCallback(async () => {
    if (isProcessing) return;

    try {
      setError(null);
      setIsProcessing(true);
      await onToggleReady();
    } catch (e) {
      console.error("Error toggling ready status:", e);
      setError(t('multiplayer_lobby.readyStatusUpdateFailed', language));
      // Auto-hide error after 5 seconds
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, onToggleReady]);

  // Ensure we have valid players array
  const validPlayers = React.useMemo(
    () => (Array.isArray(players) ? players.filter((p) => p != null) : []),
    [players],
  );

  // Render error message if there is one
  const renderError = () =>
    error ? (
      <View style={tw`bg-red-100 border-l-4 border-red-500 p-4 mb-4`}>
        <Text style={tw`text-red-700`}>{error}</Text>
      </View>
    ) : null;

  if (validPlayers.length === 0) {
    return (
      <View style={tw`flex-1 items-center justify-center`}>
        <Text style={[tw``, { color: iqesColors.mutedWarm }]}>{t('multiplayer_lobby.noPlayers', language)}</Text>
      </View>
    );
  }

  const renderItem = ({ item: player, index }: { item: Player; index: number }) => {
    if (!player) return null;
    const isCurrentPlayer = player.id === currentUserId;
    const key = player.id ?? `temp-${index}`;
    
    return (
      <View
        key={key}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          backgroundColor: 'rgba(8, 10, 24, 0.88)',
          borderRadius: 16,
          padding: 16,
          marginBottom: 12,
          borderWidth: 1,
          borderColor: 'rgba(246, 190, 58, 0.34)',
          shadowColor: iqesColors.gold,
          shadowOffset: { width: 0, height: 6 },
          shadowOpacity: 0.12,
          shadowRadius: 10,
          elevation: 5,
        }}
      >
        {/* Avatar */}
        <View style={tw`relative`}>
          <PlayerAvatar
            nickname={getPlayerDisplayName(player)}
            size="md"
            profilePictureUrl={player.avatar_url}
          />
          {player.id && player.id === hostId && (
            <View style={tw`absolute -top-1 -right-1 bg-amber-400 rounded-full p-1`}>
              <Ionicons name="star" size={12} color="#FFFFFF" />
            </View>
          )}
        </View>

        {/* Info */}
        <View style={{ marginLeft: 12, flex: 1 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Text style={{
              fontWeight: '700',
              fontSize: 18,
              color: iqesColors.ivory,
            }}>{getPlayerDisplayName(player)}</Text>
            {player.id && player.id === hostId && (
              <Tooltip
                visible={tooltipHostIndex === index}
                onClose={() => setTooltipHostIndex(-1)}
                text={uiText('hostBadgeHint')}
              >
                <Text
                  style={tw`text-xs text-amber-600 ml-2 font-semibold`}
                  onLongPress={() => setTooltipHostIndex(index)}
                >
                  {t('multiplayer_lobby.host', language)}
                </Text>
              </Tooltip>
            )}
            {isCurrentPlayer && (
              <Tooltip
                visible={tooltipYouIndex === index}
                onClose={() => setTooltipYouIndex(-1)}
                text={uiText('youBadgeHint')}
              >
                <View style={[tw`ml-2 px-2 py-0.5 rounded-full`, { backgroundColor: iqesColors.royalPurple }]}>
                  <Text
                    style={tw`text-xs text-white font-bold`}
                    onLongPress={() => setTooltipYouIndex(index)}
                  >
                    {t('multiplayer_lobby.you', language)}
                  </Text>
                </View>
              </Tooltip>
            )}
          </View>

          {/* Badges */}
          <View style={tw`flex-row flex-wrap mt-1`}>
            <Tooltip
              visible={tooltipReadyIndex === index}
              onClose={() => setTooltipReadyIndex(-1)}
              text={(isCurrentPlayer ? isCurrentPlayerReady : player.is_ready) ? "This player is ready to start the game." : "This player is not ready yet."}
            >
              <View
                style={[
                  tw`rounded-full px-2 py-0.5 mr-2 mb-1`,
                  {
                    backgroundColor: (isCurrentPlayer ? isCurrentPlayerReady : player.is_ready)
                      ? 'rgba(30, 122, 40, 0.42)'
                      : 'rgba(255, 248, 232, 0.09)',
                    borderWidth: 1,
                    borderColor: (isCurrentPlayer ? isCurrentPlayerReady : player.is_ready)
                      ? 'rgba(157, 248, 113, 0.45)'
                      : 'rgba(246, 190, 58, 0.22)',
                  },
                ]}
              >
                <Text
                  style={[
                    tw`text-xs font-semibold`,
                    {
                      color: (isCurrentPlayer ? isCurrentPlayerReady : player.is_ready)
                        ? '#D1FAE5'
                        : iqesColors.mutedWarm,
                    },
                  ]}
                  onLongPress={() => setTooltipReadyIndex(index)}
                >
                  {(isCurrentPlayer ? isCurrentPlayerReady : player.is_ready) ? t('multiplayer_lobby.ready', language) : t('multiplayer_lobby.waitingStatus', language)}
                </Text>
              </View>
            </Tooltip>
            {player.joined_at && (
              <View style={[tw`rounded-full px-2 py-0.5 mb-1`, { flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255, 248, 232, 0.09)' }]}>
                <Ionicons name="time-outline" size={12} color={iqesColors.mutedWarm} style={{ marginRight: 3 }} />
                <Text style={[tw`text-xs font-semibold`, { color: iqesColors.mutedWarm }]}>
                  {new Date(player.joined_at).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* Ready button for current player */}
        {isCurrentPlayer && (
          <TouchableOpacity
            style={{
              paddingHorizontal: 24,
              paddingVertical: 16,
              borderRadius: 16,
              minHeight: 52,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isCurrentPlayerReady ? iqesColors.green : iqesColors.royalPurple,
              borderWidth: 1,
              borderColor: isCurrentPlayerReady ? 'rgba(157, 248, 113, 0.45)' : 'rgba(246, 190, 58, 0.34)',
              shadowColor: iqesColors.gold,
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: (loadingStart || isProcessing) ? 0.03 : 0.06,
              shadowRadius: 3,
              elevation: (loadingStart || isProcessing) ? 1 : 2,
              opacity: (loadingStart || isProcessing) ? 0.7 : 1,
            }}
            onPress={handleToggleReady}
            disabled={loadingStart || isProcessing}
            activeOpacity={0.85}
            accessibilityLabel={
              isCurrentPlayerReady
                ? "You are ready! Tap to un-ready."
                : "Tap to mark yourself as ready."
            }
            accessibilityRole="button"
          >
            {loadingStart || isProcessing ? (
              <>
                <ActivityIndicator color={isCurrentPlayerReady ? "#059669" : "#2563eb"} />
                <Text style={tw`text-base font-bold ml-2`}>
                  {loadingStart ? "Starting..." : "Updating..."}
                </Text>
              </>
            ) : (
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                <Ionicons
                  name={isCurrentPlayerReady ? "checkmark-circle" : "checkmark-circle-outline"}
                  size={20}
                  color="#FFFFFF"
                  style={{ marginRight: 6 }}
                />
                <Text style={{
                  color: '#FFFFFF',
                  fontSize: 16,
                  fontWeight: '700',
                  textAlign: 'center',
                }}>
                  {isCurrentPlayerReady ? t('multiplayer_lobby.readyButton', language) : t('multiplayer_lobby.iAmReadyButton', language)}
                </Text>
              </View>
            )}
          </TouchableOpacity>
        )}
      </View>
    );
  };

  return (
    <>
      {renderError()}
      <FlashList
        data={validPlayers}
        renderItem={renderItem}
        keyExtractor={(item: Player, idx) => `player-${item?.id || `temp-${idx}`}`}
        contentContainerStyle={tw`pb-4 p-4`}
        keyboardShouldPersistTaps="handled"
      />
    </>
  );
};

export default PlayersList;
